import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { timeBasedTheme } from '../lib/themeSchedule';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
    theme: Theme;
    setTheme: (theme: Theme, options?: { persist?: boolean }) => void;
    currentTheme: 'light' | 'dark'; // The actual resolved theme
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Única forma de fijar el tema en el documento: clase `.dark` en <html> (el preset de tokens usa
 * darkMode: 'class'; tokens.css también acepta [data-theme="dark"], pero no se escribe para no tener
 * dos fuentes que se desincronicen). La usan ThemeProvider e index.html (script previo al render).
 */
export function applyThemeClass(theme: 'light' | 'dark', root: HTMLElement = document.documentElement): void {
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.removeAttribute('data-theme');
}

// Only reads/writes the 'theme_user_choice' key — never the old 'theme' key.
// The old 'theme' key was auto-synced from the user profile, which caused
// profile's 'light' setting to override time-based logic for all visitors.
const USER_CHOICE_KEY = 'theme_user_choice';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();

    // Initialize from explicit user choice in localStorage
    const getInitialTheme = (): Theme => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(USER_CHOICE_KEY);
            if (saved === 'light' || saved === 'dark' || saved === 'system') {
                return saved;
            }
        }
        return 'system'; // Default: time-based
    };

    const [theme, setThemeState] = useState<Theme>(getInitialTheme);

    // Explicit user action: save to localStorage with the dedicated key
    // persist = true (default): writes/clears localStorage (user chose explicitly via Settings)
    // persist = false: only updates in-memory state for temp overrides (e.g. Live Wall kiosk mode)
    const setTheme = (newTheme: Theme, options: { persist?: boolean } = {}) => {
        const { persist = true } = options;
        setThemeState(newTheme);
        if (!persist) return;
        if (newTheme === 'system') {
            // 'system' = horario 6-18 light / 18-6 dark; no se guarda, solo se borra la elección explícita
            localStorage.removeItem(USER_CHOICE_KEY);
        } else {
            localStorage.setItem(USER_CHOICE_KEY, newTheme);
        }
    };

    // Sync theme from user profile when available (API response)
    // IMPORTANT: Do NOT write to localStorage here — profile sync should not
    // override the system (time-based) default for users who haven't explicitly chosen a theme.
    useEffect(() => {
        if (user?.theme_mode) {
            const userTheme = user.theme_mode as Theme;
            setThemeState(userTheme);
            // No localStorage.setItem here intentionally
        }
    }, [user]);

    // 'system' = horario SyncLyst (light 6-18, dark 18-6, hora local), igual en .com y .mx.
    // Se revisa cada minuto para cambiar solo al cruzar las 6:00 / 18:00.
    const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => timeBasedTheme());

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const tick = () => setSystemTheme(timeBasedTheme());
        tick();
        const id = window.setInterval(tick, 60 * 1000);
        document.addEventListener('visibilitychange', tick);
        return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', tick); };
    }, []);

    const currentTheme = theme === 'system' ? systemTheme : theme;

    // Apply theme to document immediately (no mounted guard — index.html pre-applies
    // the class via inline script to prevent flash of wrong theme)
    useEffect(() => {
        applyThemeClass(currentTheme);
    }, [currentTheme]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, currentTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

/** Igual que useTheme, pero sin lanzar fuera del provider (para hooks compartidos). */
export const useOptionalTheme = () => useContext(ThemeContext);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
